/**
 * UpdateBanner.tsx -- Chrome banner shown when the build identifier is stale.
 *
 * PRD SS10: Polls meta:getBuildId (or reads from a meta tag) and compares with
 * the build-time identifier. When mismatched, shows a reload prompt.
 */
import React, { useCallback, useState, type CSSProperties } from "react";
import { Icon } from "../primitives";

export interface UpdateBannerProps {
  /** Current build identifier embedded in the HTML at build time. */
  currentBuildId: string;
  /** Latest build identifier fetched from the server. */
  latestBuildId: string | null;
}

export function UpdateBanner({
  currentBuildId,
  latestBuildId,
}: UpdateBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  const isStale =
    latestBuildId !== null &&
    latestBuildId !== "" &&
    latestBuildId !== currentBuildId;

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  if (!isStale || dismissed) return null;

  const barStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "10px 16px",
    backgroundColor: "var(--color-surface-sora-tint, rgba(92,184,219,0.15))",
    borderBottom: "1px solid var(--color-surface-sora, #5CB8DB)",
    fontSize: "14px",
    fontFamily: "var(--font-display)",
    color: "var(--color-surface-sumi, #1A1614)",
  };

  const reloadBtnStyle: CSSProperties = {
    padding: "4px 12px",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    backgroundColor: "var(--color-surface-sora, #5CB8DB)",
    color: "#FFFFFF",
  };

  const dismissBtnStyle: CSSProperties = {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    color: "var(--color-surface-sumi, #1A1614)",
    opacity: 0.5,
    padding: "2px 6px",
    lineHeight: 1,
  };

  return (
    <div style={barStyle} role="status" aria-live="polite">
      <span>A new version is available.</span>
      <button type="button" style={reloadBtnStyle} onClick={handleReload}>
        Reload
      </button>
      <button
        type="button"
        style={dismissBtnStyle}
        onClick={() => setDismissed(true)}
        aria-label="Dismiss update notice"
      >
        <Icon name="x" size={16} />
      </button>
    </div>
  );
}

export default UpdateBanner;
